import { LoaderFunctionArgs } from "@remix-run/node";
import { getUserSession } from "~/.server/session";
import getSymbolFromCurrency from "currency-symbol-map";
import { Profile } from "@puraidoco/agenthq-sdk/api";

// Define currencies and their associated countries
const currencyRegions = {
  EUR: [
    "AT",
    "BE",
    "CY",
    "EE",
    "FI",
    "FR",
    "DE",
    "GR",
    "IE",
    "IT",
    "LV",
    "LT",
    "LU",
    "MT",
    "NL",
    "PT",
    "SK",
    "SI",
    "ES",
  ],
  AUD: ["AU", "CX", "CC", "NF", "KI", "NR", "TV"],
  CAD: ["CA", "PM", "BM", "GL"],
  GBP: ["GB"],
  NZD: ["NZ", "CK", "NU", "PN", "TK"],
};

// Build the reverse mapping for O(1) lookup
const countryToCurrencyMap: Record<string, string> = {};
for (const [currency, countries] of Object.entries(currencyRegions)) {
  for (const country of countries) {
    countryToCurrencyMap[country] = currency;
  }
}

// Helper function to get currency for a country
export function getCurrencyForCountry(countryCode: string): string {
  return countryToCurrencyMap[countryCode] || "USD";
}

export interface PlanVariant {
  id: string; // unique identifier for this variant
  label?: string; // e.g., "Special Offer", "Best Value"
  secondary_label?: string; // e.g., "First time buyer discount"
  isDefault?: boolean; // If true, this variant will be selected by default when shown
  prices: {
    [currency: string]: {
      amount: number; // in cents
      base_amount?: number; // for strikethrough display
    };
  };
  metadata?: {
    name: string;
    description: string;
  };
}

interface PlanDefinition {
  baseCredits: number;
  variants: {
    default: PlanVariant;
    first_purchase?: PlanVariant;
    second_purchase?: PlanVariant;
    third_purchase?: PlanVariant;
    legacy?: PlanVariant;
    upsell?: PlanVariant;
  };
}

// Plan definitions - add more plans here
export const planDefinitions: PlanDefinition[] = [
  {
    baseCredits: 70,
    variants: {
      default: {
        id: "standard-70",
        prices: {
          aud: { amount: 2999 },
          cad: { amount: 2999 },
          eur: { amount: 1999 },
          gbp: { amount: 1499 },
          nzd: { amount: 3499 },
          usd: { amount: 1999 },
        },
        metadata: {
          name: "70 Credits",
          description: "70 credits package",
        },
      },
      first_purchase: {
        id: "first-time-70",
        label: "Special Offer",
        secondary_label: "85% off!",
        isDefault: true, // This will be selected by default for first-time buyers
        prices: {
          aud: { amount: 399, base_amount: 2999 },
          cad: { amount: 399, base_amount: 2999 },
          eur: { amount: 299, base_amount: 1999 },
          gbp: { amount: 299, base_amount: 1499 },
          nzd: { amount: 499, base_amount: 3499 },
          usd: { amount: 299, base_amount: 1999 },
        },
        metadata: {
          name: "First Time Special - 70 Credits",
          description: "70 credits package",
        },
      },
      legacy: {
        id: "legacy-70",
        prices: {
          aud: { amount: 1499 },
          cad: { amount: 1499 },
          eur: { amount: 1499 },
          gbp: { amount: 1499 },
          nzd: { amount: 1499 },
          usd: { amount: 1499 },
        },
        metadata: {
          name: "70 Credits",
          description: "70 credits package",
        },
      },
    },
  },
  {
    baseCredits: 175,
    variants: {
      default: {
        id: "standard-175",
        secondary_label: "20% off",
        prices: {
          aud: { amount: 5999, base_amount: 7499 },
          cad: { amount: 5999, base_amount: 7499 },
          eur: { amount: 3999, base_amount: 4999 },
          gbp: { amount: 2999, base_amount: 3699 },
          nzd: { amount: 6999, base_amount: 8699 },
          usd: { amount: 3999, base_amount: 4999 },
        },
        metadata: {
          name: "175 Credits",
          description: "175 credits package",
        },
      },
      second_purchase: {
        id: "second-time-175",
        label: "Special Offer",
        secondary_label: "50% off!",
        isDefault: true, // This will be selected by default for second-time buyers
        prices: {
          aud: { amount: 3699, base_amount: 7499 },
          cad: { amount: 3699, base_amount: 7499 },
          eur: { amount: 2499, base_amount: 4999 },
          gbp: { amount: 1999, base_amount: 3699 },
          nzd: { amount: 3999, base_amount: 8699 },
          usd: { amount: 2499, base_amount: 4999 },
        },
        metadata: {
          name: "Second Time Special - 175 Credits",
          description: "175 credits package",
        },
      },
      third_purchase: {
        id: "third-time-175",
        label: "Special Offer",
        secondary_label: "40% off!",
        prices: {
          aud: { amount: Math.round(7499 * 0.6), base_amount: 7499 },
          cad: { amount: Math.round(7499 * 0.6), base_amount: 7499 },
          eur: { amount: Math.round(4999 * 0.6), base_amount: 4999 },
          gbp: { amount: Math.round(3699 * 0.6), base_amount: 3699 },
          nzd: { amount: Math.round(8699 * 0.6), base_amount: 8699 },
          usd: { amount: Math.round(4999 * 0.6), base_amount: 4999 },
        },
        metadata: {
          name: "Third Time Special - 175 Credits",
          description: "175 credits package",
        },
      },
      legacy: {
        id: "legacy-175",
        prices: {
          aud: { amount: 3499 },
          cad: { amount: 3499 },
          eur: { amount: 3499 },
          gbp: { amount: 3499 },
          nzd: { amount: 3499 },
          usd: { amount: 3499 },
        },
        metadata: {
          name: "175 Credits",
          description: "175 credits package",
        },
      },
      upsell: {
        id: "upsell-175",
        label: "Upgrade Offer",
        secondary_label: "Get 175 credits for an additional 30% off",
        prices: {
          aud: { amount: Math.round(5999 * 0.7), base_amount: 7499 },
          cad: { amount: Math.round(5999 * 0.7), base_amount: 7499 },
          eur: { amount: Math.round(3999 * 0.7), base_amount: 4999 },
          gbp: { amount: Math.round(2999 * 0.7), base_amount: 3699 },
          nzd: { amount: Math.round(6999 * 0.7), base_amount: 8699 },
          usd: { amount: Math.round(3999 * 0.7), base_amount: 4999 },
        },
        metadata: {
          name: "175 Credits - Upgrade Offer",
          description: "175 credits package - Upgrade Offer",
        },
      },
    },
  },
  {
    baseCredits: 350,
    variants: {
      default: {
        id: "standard-350",
        secondary_label: "30% off",
        prices: {
          aud: { amount: 9999, base_amount: 14999 },
          cad: { amount: 9999, base_amount: 14999 },
          eur: { amount: 6999, base_amount: 9999 },
          gbp: { amount: 4999, base_amount: 7499 },
          nzd: { amount: 11999, base_amount: 17499 },
          usd: { amount: 6999, base_amount: 9999 },
        },
        metadata: {
          name: "350 Credits",
          description: "350 credits package",
        },
      },
      third_purchase: {
        id: "third-time-350",
        label: "Special Offer",
        secondary_label: "40% off!",
        isDefault: true,
        prices: {
          aud: { amount: Math.round(14999 * 0.6), base_amount: 14999 },
          cad: { amount: Math.round(14999 * 0.6), base_amount: 14999 },
          eur: { amount: Math.round(9999 * 0.6), base_amount: 9999 },
          gbp: { amount: Math.round(7499 * 0.6), base_amount: 7499 },
          nzd: { amount: Math.round(17499 * 0.6), base_amount: 17499 },
          usd: { amount: Math.round(9999 * 0.6), base_amount: 9999 },
        },
        metadata: {
          name: "Third Time Special - 350 Credits",
          description: "350 credits package",
        },
      },
      legacy: {
        id: "legacy-350",
        prices: {
          aud: { amount: 6499 },
          cad: { amount: 6499 },
          eur: { amount: 6499 },
          gbp: { amount: 6499 },
          nzd: { amount: 6499 },
          usd: { amount: 6499 },
        },
        metadata: {
          name: "350 Credits",
          description: "350 credits package",
        },
      },
      upsell: {
        id: "upsell-350",
        label: "Upgrade Offer",
        secondary_label: "Get 350 credits for an additional 30% off",
        prices: {
          aud: { amount: Math.round(9999 * 0.7), base_amount: 14999 },
          cad: { amount: Math.round(9999 * 0.7), base_amount: 14999 },
          eur: { amount: Math.round(6999 * 0.7), base_amount: 9999 },
          gbp: { amount: Math.round(4999 * 0.7), base_amount: 7499 },
          nzd: { amount: Math.round(11999 * 0.7), base_amount: 17499 },
          usd: { amount: Math.round(6999 * 0.7), base_amount: 9999 },
        },
        metadata: {
          name: "350 Credits - Upgrade Offer",
          description: "350 credits package - Upgrade Offer",
        },
      },
    },
  },
  {
    baseCredits: 700,
    variants: {
      default: {
        id: "standard-700",
        label: "Most Purchased Package",
        secondary_label: "40% off",
        prices: {
          aud: { amount: 17999, base_amount: 29999 },
          cad: { amount: 17999, base_amount: 29999 },
          eur: { amount: 11999, base_amount: 19999 },
          gbp: { amount: 8999, base_amount: 14999 },
          nzd: { amount: 20999, base_amount: 34999 },
          usd: { amount: 11999, base_amount: 19999 },
        },
        metadata: {
          name: "700 Credits",
          description: "700 credits package",
        },
      },
      legacy: {
        id: "legacy-700",
        prices: {
          aud: { amount: 11999 },
          cad: { amount: 11999 },
          eur: { amount: 11999 },
          gbp: { amount: 11999 },
          nzd: { amount: 11999 },
          usd: { amount: 11999 },
        },
        metadata: {
          name: "700 Credits",
          description: "700 credits package",
        },
      },
      upsell: {
        id: "upsell-700",
        label: "Upgrade Offer",
        secondary_label: "Get 700 credits for an additional 30% off",
        prices: {
          aud: { amount: Math.round(17999 * 0.7), base_amount: 29999 },
          cad: { amount: Math.round(17999 * 0.7), base_amount: 29999 },
          eur: { amount: Math.round(11999 * 0.7), base_amount: 19999 },
          gbp: { amount: Math.round(8999 * 0.7), base_amount: 14999 },
          nzd: { amount: Math.round(20999 * 0.7), base_amount: 34999 },
          usd: { amount: Math.round(11999 * 0.7), base_amount: 19999 },
        },
        metadata: {
          name: "700 Credits - Upgrade Offer",
          description: "700 credits package - Upgrade Offer",
        },
      },
    },
  },
  {
    baseCredits: 1400,
    variants: {
      default: {
        id: "standard-1400",
        label: "Best Value",
        secondary_label: "50% off",
        prices: {
          aud: { amount: 29999, base_amount: 59999 },
          cad: { amount: 29999, base_amount: 59999 },
          eur: { amount: 19999, base_amount: 39999 },
          gbp: { amount: 14999, base_amount: 29999 },
          nzd: { amount: 34999, base_amount: 69999 },
          usd: { amount: 19999, base_amount: 39999 },
        },
        metadata: {
          name: "1400 Credits",
          description: "1400 credits package",
        },
      },
      legacy: {
        id: "legacy-1400",
        prices: {
          aud: { amount: 19999 },
          cad: { amount: 19999 },
          eur: { amount: 19999 },
          gbp: { amount: 19999 },
          nzd: { amount: 19999 },
          usd: { amount: 19999 },
        },
        metadata: {
          name: "1400 Credits",
          description: "1400 credits package",
        },
      },
      upsell: {
        id: "upsell-1400",
        label: "Upgrade Offer",
        secondary_label: "Get 1400 credits for an additional 30% off",
        prices: {
          aud: { amount: Math.round(29999 * 0.7), base_amount: 59999 },
          cad: { amount: Math.round(29999 * 0.7), base_amount: 59999 },
          eur: { amount: Math.round(19999 * 0.7), base_amount: 39999 },
          gbp: { amount: Math.round(14999 * 0.7), base_amount: 29999 },
          nzd: { amount: Math.round(34999 * 0.7), base_amount: 69999 },
          usd: { amount: Math.round(19999 * 0.7), base_amount: 39999 },
        },
        metadata: {
          name: "1400 Credits - Upgrade Offer",
          description: "1400 credits package - Upgrade Offer",
        },
      },
    },
  },
];

// Plan interface for UI consumption (matches existing code expectations)
export interface Plan {
  id: string;
  credits: number;
  amount: number; // in dollars
  base_amount: number; // in dollars
  currency: string;
  symbol?: string;
  label?: string;
  secondary_label?: string;
  isDefaultSelection?: boolean; // Indicates this plan should be selected by default
}

function getUnifiedPlans(profile: Profile, currency: string): Plan[] {
  const currencyLower = currency.toLowerCase();

  // Check user eligibility for special offers
  const isLegacyUser =
    new Date(profile.created_at) < new Date("2025-05-01") &&
    profile.purchases_count > 5;

  const isFirstTimeBuyer = profile.purchases_count === 0;
  const isSecondTimeBuyer = profile.purchases_count === 1;
  const isThirdTimeBuyer = profile.purchases_count === 2;

  // Build the plan list based on user eligibility
  const plans: Plan[] = [];

  for (const planDef of planDefinitions) {
    let variant: PlanVariant | undefined;

    // Select the appropriate variant based on user state
    if (isLegacyUser && planDef.variants.legacy) {
      variant = planDef.variants.legacy;
    } else if (isFirstTimeBuyer && planDef.variants.first_purchase) {
      variant = planDef.variants.first_purchase;
    } else if (isSecondTimeBuyer && planDef.variants.second_purchase) {
      variant = planDef.variants.second_purchase;
    } else if (isThirdTimeBuyer && planDef.variants.third_purchase) {
      variant = planDef.variants.third_purchase;
    } else {
      variant = planDef.variants.default;
    }

    // Get pricing for the user's currency
    const pricing = variant.prices[currencyLower] || variant.prices.usd;
    if (!pricing) continue;

    // Convert to Plan format expected by UI
    plans.push({
      id: variant.id,
      credits: planDef.baseCredits,
      amount: pricing.amount / 100, // Convert cents to dollars
      base_amount: (pricing.base_amount || pricing.amount) / 100,
      currency: currencyLower,
      symbol: getSymbolFromCurrency(currency.toUpperCase()) || "$",
      label: variant.label,
      secondary_label: variant.secondary_label,
      isDefaultSelection: variant.isDefault || false,
    });
  }

  // Sort plans by credits ascending
  const sortedPlans = plans.sort((a, b) => a.credits - b.credits);

  // If no plan is marked as default, select the first one
  const hasDefaultSelection = sortedPlans.some((p) => p.isDefaultSelection);
  if (!hasDefaultSelection && sortedPlans.length > 0) {
    sortedPlans[0].isDefaultSelection = true;
  }

  return sortedPlans;
}

export function getUnifiedPlanById(id: string) {
  // Unlimited promo SKUs live outside planDefinitions because they have no
  // variants and credits=0.
  if (id === UNLIMITED_SECOND_BUY_PLAN_ID) {
    return { ...UNLIMITED_SECOND_BUY_PLAN, credits: 0 };
  }
  if (id === UNLIMITED_RENEWAL_PLAN_ID) {
    return { ...UNLIMITED_RENEWAL_PLAN, credits: 0 };
  }
  const allPlans = planDefinitions.flatMap((def) =>
    Object.values(def.variants).map((variant) => ({
      ...variant,
      credits: def.baseCredits,
    }))
  );

  return allPlans.find((plan) => plan.id === id);
}

// =====================================================================
// Unlimited Messages Promotion (single hardcoded SKU)
//
// Two time-based packages, both 60-minute windows. Visibility/eligibility is
// enforced server-side in the _app loader and the backend purchase gate; this
// catalog just defines the prices.
//
//   - second_buy: the normal returning-customer offer ($9.99 USD)
//   - renewal:   30%-off one-shot pitched from the expiry modal ($6.99 USD)
//
// Keep these in sync with backend/api/api_v1/profiles.go (unlimitedPackage*
// constants and the duration map).
// =====================================================================
export const UNLIMITED_SECOND_BUY_PLAN_ID = "unlimited_60min_second_buy";
export const UNLIMITED_RENEWAL_PLAN_ID = "unlimited_60min_renewal";

// USD-anchored. Every currency ends in .99 for consistency with the credit-pack
// pricing style.
export const UNLIMITED_SECOND_BUY_PLAN: PlanVariant = {
  id: UNLIMITED_SECOND_BUY_PLAN_ID,
  prices: {
    aud: { amount: 1499 }, // A$14.99
    cad: { amount: 1399 }, // C$13.99
    eur: { amount: 899 },  // €8.99
    gbp: { amount: 799 },  // £7.99
    nzd: { amount: 1599 }, // NZ$15.99
    usd: { amount: 999 },  // $9.99 — primary
  },
  metadata: {
    name: "60 Minutes Unlimited",
    description: "Send unlimited messages for 60 minutes",
  },
};

// 30%-off renewal pitched when the user's previous unlimited window expires.
export const UNLIMITED_RENEWAL_PLAN: PlanVariant = {
  id: UNLIMITED_RENEWAL_PLAN_ID,
  prices: {
    aud: { amount: 999 },  // A$9.99
    cad: { amount: 999 },  // C$9.99
    eur: { amount: 599 },  // €5.99
    gbp: { amount: 599 },  // £5.99
    nzd: { amount: 1099 }, // NZ$10.99
    usd: { amount: 699 },  // $6.99 — primary
  },
  metadata: {
    name: "60 Minutes Unlimited (Renewal)",
    description: "30% off — extend your unlimited session for 60 minutes",
  },
};

type UnlimitedPlanShape = {
  id: string;
  name: string;
  amount: number;
  currency: string;
  durationMinutes: number;
};

function resolveUnlimitedPlan(
  plan: PlanVariant,
  currency: string,
): UnlimitedPlanShape | null {
  const lower = currency.toLowerCase();
  const price = plan.prices[lower];
  if (!price) return null;
  return {
    id: plan.id,
    name: plan.metadata?.name ?? "60 Minutes Unlimited",
    amount: price.amount,
    currency: lower,
    durationMinutes: 60,
  };
}

export function getUnlimitedSecondBuyPlan(currency: string) {
  return resolveUnlimitedPlan(UNLIMITED_SECOND_BUY_PLAN, currency);
}

export function getUnlimitedRenewalPlan(currency: string) {
  return resolveUnlimitedPlan(UNLIMITED_RENEWAL_PLAN, currency);
}

// Get upsell offer for a purchased plan using the upsell variants
export function getUpsellOfferForPlan(
  purchasedPlanId: string,
  currency: string
): {
  planId: string;
  credits: number;
  originalPrice: number;
  discountedPrice: number;
  discountPercent: number;
  label: string;
  currency: string;
  metadata?: { name: string; description: string };
} | null {
  const currencyLower = currency.toLowerCase();

  // Find the purchased plan definition
  const purchasedPlan = getUnifiedPlanById(purchasedPlanId);
  if (!purchasedPlan) return null;

  // Find the plan definition for the purchased plan
  const purchasedPlanDef = planDefinitions.find((def) =>
    Object.values(def.variants).some(
      (variant) => variant?.id === purchasedPlanId
    )
  );
  if (!purchasedPlanDef) return null;

  // Look for upsell offers in order of preference:
  // 1. First, try to find the next tier plan with an upsell variant
  const nextTierPlan = planDefinitions
    .filter((def) => def.baseCredits > purchasedPlanDef.baseCredits)
    .sort((a, b) => a.baseCredits - b.baseCredits)[0]; // Get the smallest next tier

  if (nextTierPlan?.variants.upsell) {
    const upsellVariant = nextTierPlan.variants.upsell;
    const pricing =
      upsellVariant.prices[currencyLower] || upsellVariant.prices.usd;
    const originalPricing =
      nextTierPlan.variants.default.prices[currencyLower] ||
      nextTierPlan.variants.default.prices.usd;

    if (pricing && originalPricing) {
      const discountedPrice = pricing.amount / 100;
      const originalPrice = originalPricing.base_amount
        ? originalPricing.base_amount / 100
        : originalPricing.amount / 100;
      const discountPercent = Math.round(
        (1 - discountedPrice / originalPrice) * 100
      );

      return {
        planId: upsellVariant.id,
        credits: nextTierPlan.baseCredits,
        originalPrice,
        discountedPrice,
        discountPercent,
        label: upsellVariant.label || "Special Upgrade Offer",
        currency: currencyLower,
        metadata: upsellVariant.metadata,
      };
    }
  }

  // 2. If no next tier or no upsell variant, check if the current plan has an upsell variant
  if (purchasedPlanDef.variants.upsell) {
    const upsellVariant = purchasedPlanDef.variants.upsell;
    const pricing =
      upsellVariant.prices[currencyLower] || upsellVariant.prices.usd;
    const originalPricing =
      purchasedPlanDef.variants.default.prices[currencyLower] ||
      purchasedPlanDef.variants.default.prices.usd;

    if (pricing && originalPricing) {
      const discountedPrice = pricing.amount / 100;
      const originalPrice = originalPricing.base_amount
        ? originalPricing.base_amount / 100
        : originalPricing.amount / 100;
      const discountPercent = Math.round(
        (1 - discountedPrice / originalPrice) * 100
      );

      return {
        planId: upsellVariant.id,
        credits: purchasedPlanDef.baseCredits,
        originalPrice,
        discountedPrice,
        discountPercent,
        label: upsellVariant.label || "Special Offer - Get More Credits",
        currency: currencyLower,
        metadata: upsellVariant.metadata,
      };
    }
  }

  return null;
}

export async function loader({ request }: LoaderFunctionArgs) {
  const user = await getUserSession(request);
  if (!user) {
    throw new Response("Unauthorized", { status: 401 });
  }

  const url = new URL(request.url);
  let provider = url.searchParams.get("provider");

  if (!provider) {
    // TODO - This should never happen. 
    console.log("[WARN] api.get-products: No provider found in URL, using default payment provider");
    provider = process.env.DEFAULT_PAYMENT_PROVIDER || '';
  }

  // Get the user's country code
  let userGeo = user.profile.country_code || user.profile.metadata.geo?.countryCode;

  if (!userGeo) {
    // TODO - This should never happen. 
    console.log("[WARN] api.get-products: No user geo found, using default user geo");
    userGeo = "US";
  }

  // Determine currency based on country
  let currency = getCurrencyForCountry(userGeo);
  if (provider.toLowerCase().includes("verotel")) {
    currency = "GBP"; // Verotel only supports GBP at the moment
  }

  // Use unified plans that automatically handle legacy, first-time, and second-time buyer logic
  const plans = getUnifiedPlans(user.profile, currency);
  return {
    plans,
    provider,
    currency,
  };
}
